import { CSSObject } from 'styled-components';
import Select from 'react-select';
import React, { FC, ReactElement, useCallback, useMemo, useRef } from 'react';
import IconCross from '@shared/icons/IconCross';
import CreatableSelect from 'react-select/creatable';

interface IStyleObject {
  [key: string]: any;
}

const createStyles = (
  needHighlightFirstOption?: boolean,
  singleValueColor?: string,
  textSize: string = '12px',
  height: string = '33px'
): IStyleObject => {
  return {
    control: (provided: any, state: any) => {
      return {
        ...provided,
        backgroundColor: '#211F24',
        minHeight: state.selectProps.isFilter ? '42px' : height,
        borderColor: 'none',
        border: 'none',
        color: 'white',
        boxShadow: 'none',
        width: '100%',
        cursor: 'pointer',
      };
    },
    dropdownIndicator: (provided: any, state: any) => ({
      ...provided,
      padding: 5,
    }),
    clearIndicator(base: CSSObject): CSSObject {
      base.padding = 0;
      base.display = 'none';
      // иконка отмены всех опшенов
      return base;
    },
    indicatorsContainer(base: CSSObject): CSSObject {
      base.padding = 10;
      base.display = 'none';
      // область открытия дропдауна

      return base;
    },
    placeholder: (base: CSSObject): CSSObject => {
      return {
        ...base,
        color: '#ffffff',
        fontSize: '12px',
      };
    },
    noOptionsMessage: (base: CSSObject): CSSObject => {
      return {
        ...base,
        backgroundColor: '#252328',
        color: 'white',
        fontSize: textSize,
      };
    },
    menu: (base: CSSObject): CSSObject => {
      return {
        ...base,
        backgroundColor: '#252328',
        color: 'white',
        border: '1px solid #211F24',
        borderRadius: '10px',
        overflow: 'hidden',
        width: '100%',
        fontSize: textSize,
        zIndex: 9999,
      };
    },
    menuPortal: (base: CSSObject): CSSObject => {
      return {
        ...base,
        zIndex: 9999,
      };
    },
    indicatorSeparator(base: CSSObject): CSSObject {
      base.padding = 0;
      base.display = 'none';
      // vertical line between icons in input

      return base;
    },
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: '#252328',
      color: 'white',
      fontSize: textSize,
      cursor: 'pointer',
      width: '100%',
      '&:first-child': {
        color: needHighlightFirstOption ? '#D7F525' : 'white',
      },
    }),
    singleValue: (provided: any, state: any) => ({
      ...provided,
      color: singleValueColor,
    }),

    input: (provided: any, state: any) => ({
      ...provided,
      input: {
        margin: '0px',
        color: 'white',
      },
      height,
      fontSize: '12px',
      paddingTop: '0px',
      width: '100%',
      color: 'white',
      paddingBottom: '0px',
      marginTop: 0,
      marginBottom: 0,
    }),
  };
};

interface ICustomSelectProps {
  options: any;
  title: string;
  isLoading: boolean;
  width?: number | string;
  height?: string;
  onChange?: (values: ISelectOption | null) => void;
  value?: ISelectOption | null;
  needHighlightFirstOption?: boolean;
  singleValueColor?: string;
  textSize?: string;
  bgColor?: string;
  hideClearIcon?: boolean;
  icon?: ReactElement;
  isCreatable?: boolean;
}

export interface ISelectOption {
  value: string;
  label: string;
}

const SingleSelect: FC<ICustomSelectProps> = ({
  options,
  title,
  onChange,
  isLoading = false,
  width = 150,
  height = '33px',
  value,
  needHighlightFirstOption = false,
  singleValueColor = 'white',
  bgColor = '#211F24',
  hideClearIcon = false,
  textSize = '12px',
  icon,
  isCreatable = false,
}) => {
  const selectRef = useRef(null);

  const styles = useMemo(() => {
    return createStyles(needHighlightFirstOption, singleValueColor, textSize, height);
  }, [needHighlightFirstOption, singleValueColor, textSize, height]);
  const menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined;

  return (
    <div
      style={{
        minWidth: `${Number.isInteger(width) ? width + 'px' : width}`,
        maxWidth: `${Number.isInteger(width) ? width + 'px' : width}`,
        height,
      }}
      className={`z-[10] rounded-[8px] text-yellow-300 text-[white] cursor-pointer relative`}
    >
      <div className="absolute left-[5px] top-[50%] -translate-y-[50%]">{icon}</div>

      {isCreatable ? (
        <CreatableSelect
          ref={selectRef}
          options={options}
          menuPosition="fixed"
          menuPortalTarget={menuPortalTarget}
          isMulti={false}
          className={icon ? 'pl-[20px]' : ''}
          openMenuOnFocus={false}
          hideSelectedOptions={true}
          closeMenuOnSelect={true}
          value={value ? value : ''}
          styles={styles}
          isClearable={true}
          isLoading={isLoading}
          formatCreateLabel={inputValue => `Создать "${inputValue}"`}
          placeholder={title}
          onChange={(options: any) => {
            if (onChange) {
              onChange(options);
            }
          }}
        />
      ) : (
        <Select
          ref={selectRef}
          options={options}
          menuPosition="fixed"
          menuPortalTarget={menuPortalTarget}
          isMulti={false}
          className={icon ? 'pl-[20px]' : ''}
          openMenuOnFocus={false}
          hideSelectedOptions={true}
          closeMenuOnSelect={true}
          value={value ? value : ''}
          styles={styles}
          isClearable={true}
          isLoading={isLoading}
          placeholder={title}
          onChange={(options: any) => {
            if (onChange) {
              onChange(options);
            }
          }}
        />
      )}
      {!value || hideClearIcon ? (
        <>
          {/* @ts-ignore */}
          <svg
            onClick={() => selectRef.current.focus()}
            className="absolute right-[10px] top-[50%] -translate-y-[50%]"
            width="7"
            height="5"
            viewBox="0 0 7 5"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.71098 1.98938L4.18608 4.24524C3.80588 4.58492 3.19172 4.58492 2.81152 4.24524L0.286622 1.98939C-0.327542 1.44066 0.111146 0.500001 0.978776 0.500001L6.02857 0.5C6.8962 0.5 7.32514 1.44066 6.71098 1.98938Z"
              fill="white"
            />
          </svg>
        </>
      ) : (
        <div
          className={`absolute right-[5px] top-[50%] -translate-y-[50%] bg-[#211F24] pl-[5px]`}
          onClick={() => {
            if (onChange) {
              onChange(null);
            }
          }}
        >
          <IconCross width={11} height={11} />
        </div>
      )}
    </div>
  );
};

export default SingleSelect;
